import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  Building2, FileText, Settings, LogOut, Check, Crown, BarChart3,
  Loader2, Sparkles, Zap, ArrowRight, Calculator, HelpCircle
} from "lucide-react";
import { toast } from "sonner";

const SubscriptionPage = ({ auth }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [billingCycle, setBillingCycle] = useState("annual"); // "monthly" or "annual"

  useEffect(() => {
    fetchTiers();
    checkPaymentStatus();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/tiers`);
      setTiers(response.data);
    } catch (error) {
      toast.error("Failed to load subscription plans");
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;

    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      try {
        const response = await axios.get(`${API}/subscriptions/status/${sessionId}`, {
          withCredentials: true
        });

        if (response.data.payment_status === "paid") {
          toast.success(`Successfully upgraded to ${response.data.tier?.toUpperCase()}!`);
          auth.checkAuth();
          navigate("/subscription", { replace: true });
          return;
        } else if (response.data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          navigate("/subscription", { replace: true });
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          toast.info("Payment processing. Please check back in a moment.");
          navigate("/subscription", { replace: true });
        }
      } catch (error) {
        toast.error("Error checking payment status");
        navigate("/subscription", { replace: true });
      }
    };

    poll();
  };

  const handleSubscribe = async (tier) => {
    setCheckoutLoading(tier);
    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        tier,
        billing_cycle: billingCycle,
        origin_url: window.location.origin
      }, { withCredentials: true });

      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Failed to start checkout");
      setCheckoutLoading(null);
    }
  };

  const tierIcons = {
    basic: Sparkles,
    pro: Zap
  };

  const tierColors = {
    basic: "teal",
    pro: "purple"
  };

  const tierBadgeColor = {
    trial: "bg-slate-100 text-slate-700",
    basic: "bg-teal-100 text-teal-700",
    pro: "bg-purple-100 text-purple-700",
    admin: "bg-amber-100 text-amber-700"
  };

  const currentTier = auth.user?.subscription_tier || "trial";
  const userBillingCycle = auth.user?.billing_cycle || "monthly";

  const getDisplayPrice = (tier) => {
    if (billingCycle === "annual") {
      return tier.annual_monthly_equivalent;
    }
    return tier.monthly_price;
  };

  const getReportLimit = (tier) => {
    if (billingCycle === "annual") {
      return `${tier.annual_reports} reports/year`;
    }
    return `${tier.monthly_reports} reports/month`;
  };

  const getCurrentReportLimit = () => {
    if (currentTier === "trial") return "5 free reports";
    if (currentTier === "admin") return "Unlimited";
    const tier = tiers.find(t => t.tier === currentTier);
    if (!tier) return "N/A";
    if (userBillingCycle === "annual") return `${tier.annual_reports}/year`;
    return `${tier.monthly_reports}/month`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-navy-900 text-white hidden lg:block">
        <div className="p-6">
          <Link to="/" className="flex items-center space-x-2">
            <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
            
          </Link>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link 
            to="/dashboard" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <BarChart3 className="h-5 w-5" />
            <span>Dashboard</span>
          </Link>
          <Link 
            to="/reports" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <FileText className="h-5 w-5" />
            <span>My Reports</span>
          </Link>
          <Link 
            to="/compare" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Calculator className="h-5 w-5" />
            <span>Compare</span>
            {currentTier !== "pro" && currentTier !== "admin" && (
              <Badge className="bg-purple-500 text-white text-xs ml-auto">PRO</Badge>
            )}
          </Link>
          <Link 
            to="/subscription" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-white/10 text-white"
          >
            <Crown className="h-5 w-5" />
            <span>Subscription</span>
          </Link>
          <Link 
            to="/support" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <HelpCircle className="h-5 w-5" />
            <span>Support</span>
          </Link>
          <Link 
            to="/settings" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Settings className="h-5 w-5" />
            <span>Settings</span>
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={auth.user?.picture} />
                <AvatarFallback className="bg-teal-500 text-navy-900">
                  {auth.user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{auth.user?.name}</p>
                <Badge className={`${tierBadgeColor[currentTier] || tierBadgeColor.basic} text-xs`}>
                  {currentTier.toUpperCase()}
                </Badge>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10"
              onClick={auth.logout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/" className="flex items-center space-x-2">
            <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
            
          </Link>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <BarChart3 className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/settings")}>
              <Settings className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8 max-w-5xl">
          {/* Header */}
          <div className="mb-8 text-center lg:text-left">
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-navy-900 mb-2">
              Subscription Plans
            </h1>
            <p className="text-slate-600">
              Choose the plan that fits your needs
            </p>
          </div>

          {/* Current Plan Banner */}
          <Card className="mb-8 bg-navy-900 text-white" data-testid="current-plan-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-slate-300 text-sm mb-1">Current Plan</p>
                  <p className="font-heading text-2xl font-bold">
                    {currentTier === "trial" ? "Free Trial" : currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
                  </p>
                </div>
                <div className="text-right">
                  <Badge className={`${tierBadgeColor[currentTier] || tierBadgeColor.basic} text-lg px-4 py-2`}>
                    {getCurrentReportLimit()}
                  </Badge>
                  {currentTier === "trial" && (
                    <p className="text-xs text-slate-400 mt-2">
                      {auth.user?.trial_reports_used || 0} of 5 free reports used
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Processing */}
          {checkingPayment && (
            <Card className="mb-8 border-teal-200 bg-teal-50">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
                  <div>
                    <p className="font-medium text-teal-900">Processing your payment...</p>
                    <p className="text-sm text-teal-700">Please wait while we confirm your subscription.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center mb-8 space-x-4">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-navy-900' : 'text-slate-500'}`}>
              Monthly
            </span>
            <Switch
              checked={billingCycle === "annual"}
              onCheckedChange={(checked) => setBillingCycle(checked ? "annual" : "monthly")}
            />
            <span className={`text-sm font-medium ${billingCycle === 'annual' ? 'text-navy-900' : 'text-slate-500'}`}>
              Annual
            </span>
            {billingCycle === "annual" && (
              <Badge className="bg-teal-100 text-teal-700">Save up to 45%</Badge>
            )}
          </div>

          {/* Pricing Cards */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {tiers.map((tier) => {
                const TierIcon = tierIcons[tier.tier] || Sparkles;
                const isCurrentPlan = currentTier === tier.tier;
                const isPro = tier.tier === "pro";
                
                return (
                  <Card 
                    key={tier.tier}
                    className={`relative ${isPro ? 'border-2 border-purple-500 shadow-xl' : 'border-2 border-teal-500'} ${isCurrentPlan ? 'ring-2 ring-navy-900 ring-offset-2' : ''}`}
                    data-testid={`plan-card-${tier.tier}`}
                  >
                    {isPro && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-sm font-medium px-4 py-1 rounded-full">
                        Best Value
                      </div>
                    )}
                    {isCurrentPlan && (
                      <div className="absolute -top-4 right-4 bg-navy-900 text-white text-xs font-medium px-3 py-1 rounded-full">
                        Current
                      </div>
                    )}
                    
                    <CardHeader className="text-center pb-4">
                      <div className={`w-12 h-12 rounded-xl ${isPro ? 'bg-purple-100' : 'bg-teal-100'} flex items-center justify-center mx-auto mb-4`}>
                        <TierIcon className={`h-6 w-6 ${isPro ? 'text-purple-600' : 'text-teal-600'}`} />
                      </div>
                      <CardTitle className="font-heading text-xl">{tier.name}</CardTitle>
                      <div className="mt-4">
                        <span className="font-data text-4xl font-bold text-navy-900">
                          ${getDisplayPrice(tier).toFixed(0)}
                        </span>
                        <span className="text-slate-500">/month</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {billingCycle === "annual" 
                          ? `$${tier.annual_price}/year billed annually`
                          : `$${tier.monthly_price}/month billed monthly`
                        }
                      </p>
                      <Badge className={`mt-2 ${isPro ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'}`}>
                        {getReportLimit(tier)}
                      </Badge>
                    </CardHeader>
                    
                    <CardContent>
                      <ul className="space-y-3 mb-6">
                        {tier.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-slate-600">
                            <Check className={`h-4 w-4 mr-3 flex-shrink-0 ${isPro ? 'text-purple-500' : 'text-teal-500'}`} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      
                      <Button
                        className={`w-full ${
                          isPro 
                            ? 'bg-purple-500 hover:bg-purple-400 text-white' 
                            : 'bg-teal-500 hover:bg-teal-400 text-navy-900'
                        }`}
                        disabled={isCurrentPlan || checkoutLoading === tier.tier}
                        onClick={() => handleSubscribe(tier.tier)}
                        data-testid={`subscribe-btn-${tier.tier}`}
                      >
                        {checkoutLoading === tier.tier ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : isCurrentPlan ? (
                          "Current Plan"
                        ) : (
                          <>
                            {currentTier === "trial" ? "Start Plan" : isPro ? "Upgrade" : "Subscribe"}
                            <ArrowRight className="h-4 w-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Free Trial Info */}
          {currentTier === "trial" && (
            <Card className="mt-8 border-slate-200 bg-slate-50" data-testid="trial-info-card">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="p-2 rounded-lg bg-slate-200">
                    <Sparkles className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-navy-900 mb-1">Free Trial</h4>
                    <p className="text-sm text-slate-600">
                      You have <strong>{5 - (auth.user?.trial_reports_used || 0)} reports remaining</strong> in your free trial. 
                      Subscribe to a paid plan for unlimited access and premium features.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pro Features Highlight */}
          <Card className="mt-8 border-purple-200 bg-purple-50/50" data-testid="pro-features-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center text-purple-900">
                <Calculator className="h-5 w-5 mr-2 text-purple-500" />
                Pro Feature: Property Comparison Calculator
              </CardTitle>
              <CardDescription className="text-purple-700">
                Compare up to 5 properties side-by-side to find the best investment opportunity
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <p className="font-semibold text-navy-900 mb-1">Side-by-Side Analysis</p>
                  <p className="text-sm text-slate-600">Compare rent estimates, market positions, and annual income potential</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <p className="font-semibold text-navy-900 mb-1">Investment Insights</p>
                  <p className="text-sm text-slate-600">Identify the highest-earning property at a glance</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <p className="font-semibold text-navy-900 mb-1">Save & Share</p>
                  <p className="text-sm text-slate-600">Keep comparison history and share with partners</p>
                </div>
              </div>
              {currentTier !== "pro" && currentTier !== "admin" && (
                <Button 
                  className="mt-6 bg-purple-500 hover:bg-purple-400 text-white"
                  onClick={() => handleSubscribe("pro")}
                >
                  Upgrade to Pro for ${billingCycle === "annual" ? "18" : "26"}/month
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </CardContent>
          </Card>

          {/* FAQ Section */}
          <Card className="mt-8" data-testid="faq-card">
            <CardHeader>
              <CardTitle className="font-heading">Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-medium text-navy-900 mb-2">Can I cancel anytime?</h4>
                <p className="text-sm text-slate-600">
                  Yes, you can cancel your subscription at any time from the <Link to="/support" className="text-teal-600 hover:underline">Support page</Link>. Your access will continue until the end of your billing period.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-navy-900 mb-2">What happens if I exceed my report limit?</h4>
                <p className="text-sm text-slate-600">
                  Reports beyond your limit are charged at $0.15 per report. You can also upgrade your plan anytime for more reports.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-navy-900 mb-2">What's the difference between monthly and annual billing?</h4>
                <p className="text-sm text-slate-600">
                  Annual billing saves you 50% compared to monthly. You're billed once per year and get more reports (100 vs 10 for Basic, 1000 vs 100 for Pro).
                </p>
              </div>
              <div>
                <h4 className="font-medium text-navy-900 mb-2">Is my payment secure?</h4>
                <p className="text-sm text-slate-600">
                  Yes, all payments are processed securely through Stripe. We never store your credit card information.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
